import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { getClientConfig } from '@shared/config/getClientConfig';
import {
  UtiqConsentVersionEntryName,
  UtiqConsentVersionPreviousEntryName,
  UtiqStatusEntryName,
} from '@shared/constants/utiq';
import { getVersions } from '@shared/helpers/getVersions';
import { setVersionCookies } from '@utiqLoader/helpers/setVersionCookies';
import { utiqMachine } from '../../machine';
import {
  triggerConsentChange,
  triggerEligibilityCheck,
} from '@machine/externalActions';
import { acceptConsent, denyConsent } from '@machine/actions';
import { PayloadObject } from '@utiqLoader/types';
import { getHostname } from '@shared/getUtiqHost';
import { checkConsentVersionCookieValue } from '@shared/cookies/checkConsentVersionCookieValue';
import { setConsentVersionBasedOnModel } from '@utiqLoader/helpers/setConsentVersionBasedOnModel';
import { callEventListeners } from '@shared/events/callEventListeners';

export const processConsentChangeFunction = async (
  Utiq: UtiqEnvironment,
  isConsentGranted: boolean,
  payload: PayloadObject,
) => {
  // Capture flag early - it gets cleared mid-function but is needed later for eligibility check
  const shouldSyncVersionCookies = Utiq.syncVersionCookies;

  if (shouldSyncVersionCookies && isConsentGranted) {
    setVersionCookies(
      getVersions(Utiq.data?.popupContent?.version!).majorVersion!,
      390,
      getVersions(Utiq.data?.popupContent?.versionPrevious!).majorVersion!,
    );
  }
  const consentVersionCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionEntryName,
  );
  const consentVersionPreviousCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionPreviousEntryName,
  );
  consoleLogger.logDebug(
    `Value of consentVersionCookie is ${consentVersionCookie}.`,
  );
  consoleLogger.logDebug(
    `Value of consentVersionPreviousCookie is ${consentVersionPreviousCookie}.`,
  );

  consoleLogger.logDebug('Consent is granted: ', isConsentGranted);

  const currentConsentStatusCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqStatusEntryName,
  );

  // TODO remove condition in the beginning of April 2025
  // START
  const hostname = getHostname();
  const consentVersion = checkConsentVersionCookieValue(
    consentVersionCookie,
    window.Utiq?.data?.popupContent?.version,
  );

  const consentVersionPrevious = checkConsentVersionCookieValue(
    consentVersionPreviousCookie,
    window.Utiq?.data?.popupContent?.versionPrevious,
  );
  const isOuestFranceUserMissIdentified =
    window.location.host === 'www.ouest-france.fr' &&
    isConsentGranted &&
    window.Utiq!.initialEligibilityValue! === false &&
    (!consentVersionCookie || Number(consentVersionCookie) < 3) && // cookie version < 3
    getVersions(window.Utiq?.data?.popupContent?.version!).majorVersion! >= 3; // template data version >= 3

  payload[hostname].consentVersion = isOuestFranceUserMissIdentified
    ? getVersions(window.Utiq?.data?.popupContent?.version!).majorVersion!
    : setConsentVersionBasedOnModel(
        window.Utiq?.initialConsentVersionCookie,
        consentVersion,
        consentVersionPrevious,
        currentConsentStatusCookie,
      );
  payload[hostname].consentVersionPrevious = isOuestFranceUserMissIdentified
    ? getVersions(window.Utiq?.data?.popupContent?.versionPrevious!)
        .majorVersion!
    : consentVersionPrevious;
  // END

  // TODO uncomment this code in the beginning of April 2025
  // payload[hostname].consentVersion = setConsentVersionBasedOnModel(
  //   window.Utiq?.initialConsentVersionCookie,
  //   consentVersion,
  //   consentVersionPrevious,
  //   currentConsentStatusCookie,
  // );
  // payload[hostname].consentVersionPrevious = consentVersionPrevious;

  /* Entry read (get) must occur before write (set) below */
  const hasConsentChanged = currentConsentStatusCookie
    ? isConsentGranted !== (currentConsentStatusCookie === 'true')
    : true;

  const versionCookiesMissing =
    !consentVersionCookie && Utiq.data?.popupContent?.version;

  const { config } = Utiq;
  if (!config) throw new Error('Utiq.config is undefined');

  if (!hasConsentChanged) {
    payload[getHostname()].consentVersion =
      getVersions(consentVersionCookie).majorVersion!;
    payload[getHostname()].consentVersionPrevious = getVersions(
      consentVersionPreviousCookie,
    ).majorVersion!;
  }

  if (
    hasConsentChanged ||
    shouldSyncVersionCookies ||
    (isConsentGranted && versionCookiesMissing)
  ) {
    if (isConsentGranted) {
      acceptConsent(
        utiqMachine.makeSnap(),
        utiqMachine.ctx,
        Utiq.data?.popupContent!,
      );
    } else {
      denyConsent(utiqMachine.makeSnap(), utiqMachine.ctx);
    }
  }

  if (isConsentGranted) {
    const needsEligibilityCheck = hasConsentChanged || shouldSyncVersionCookies;
    if (
      needsEligibilityCheck &&
      !(await triggerEligibilityCheck({
        trigger: 'consent_granted',
        isConsentGranted: true,
        ignoreInterval: true,
        hasConsentChanged,
      }))
    ) {
      consoleLogger.logWarn(
        'Consent was granted, but service eligibility check failed for current connection type. Service will not be loaded.',
      );
      callEventListeners('onFlowCompleted', 'Utiq', {});
      return;
    }

    getClientConfig(config.utiqHost);

    utiqMachine.ctx.times.__retriggerIdConnectFlow = new Date(Date.now());
    await triggerConsentChange(true, payload);

    if (Utiq.data?.showRevocationErrorMessage) {
      Utiq.data.showRevocationErrorMessage = false;
    }
  } else {
    getClientConfig(config.utiqHost);
    utiqMachine.ctx.times.consentExternalyDenied = new Date(Date.now());

    await triggerConsentChange(false, payload);
    if (hasConsentChanged) {
      callEventListeners('onFlowCompleted', 'Utiq', {});
    }
  }

  if (Utiq.isInitialPageLoad) {
    Utiq.isInitialPageLoad = false;
  }
};
